import { injectable, inject } from 'inversify';
import type {
  CreateArticlePayload,
  CreateCategoryPayload,
  HelpArticleBundleDTO,
  HelpArticleDTO,
  HelpCategoryDTO,
  HelpLayoutDTO,
  IHelpDocumentationRepository,
  ListArticlesParams,
  SaveHelpLayoutPayload,
  UpdateArticlePayload,
  UpdateCategoryPayload,
  UpsertArticleBundlePayload,
} from '@/domain/repositories/IHelpDocumentationRepository';
import { TYPES } from '@/di/tokens';

@injectable()
export class ListHelpCategoriesAction {
  constructor(
    @inject(TYPES.IHelpDocumentationRepository) private repo: IHelpDocumentationRepository
  ) {}

  execute(): Promise<HelpCategoryDTO[]> {
    return this.repo.listCategories();
  }
}

@injectable()
export class SaveHelpCategoryAction {
  constructor(
    @inject(TYPES.IHelpDocumentationRepository) private repo: IHelpDocumentationRepository
  ) {}

  create(data: CreateCategoryPayload): Promise<HelpCategoryDTO> {
    return this.repo.createCategory(data);
  }

  update(id: string, data: UpdateCategoryPayload): Promise<HelpCategoryDTO> {
    return this.repo.updateCategory(id, data);
  }

  delete(id: string): Promise<{ deleted: 'soft' | 'hard' }> {
    return this.repo.deleteCategory(id);
  }
}

@injectable()
export class ListHelpArticlesAction {
  constructor(
    @inject(TYPES.IHelpDocumentationRepository) private repo: IHelpDocumentationRepository
  ) {}

  execute(params?: ListArticlesParams): Promise<HelpArticleDTO[]> {
    return this.repo.listArticles(params);
  }
}

@injectable()
export class GetHelpArticleAction {
  constructor(
    @inject(TYPES.IHelpDocumentationRepository) private repo: IHelpDocumentationRepository
  ) {}

  execute(id: string): Promise<HelpArticleDTO> {
    return this.repo.getArticle(id);
  }
}

@injectable()
export class GetHelpArticleBundleAction {
  constructor(
    @inject(TYPES.IHelpDocumentationRepository) private repo: IHelpDocumentationRepository
  ) {}

  byId(id: string): Promise<HelpArticleBundleDTO> {
    return this.repo.getBundleById(id);
  }

  bySlug(slug: string): Promise<HelpArticleBundleDTO> {
    return this.repo.getBundleBySlug(slug);
  }
}

@injectable()
export class SaveHelpArticleBundleAction {
  constructor(
    @inject(TYPES.IHelpDocumentationRepository) private repo: IHelpDocumentationRepository
  ) {}

  execute(data: UpsertArticleBundlePayload): Promise<HelpArticleBundleDTO> {
    return this.repo.upsertBundle(data);
  }
}

@injectable()
export class TranslateHelpTextAction {
  constructor(
    @inject(TYPES.IHelpDocumentationRepository) private repo: IHelpDocumentationRepository
  ) {}

  execute(text: string, from: string, to: string): Promise<string> {
    return this.repo.translateText(text, from, to);
  }
}

@injectable()
export class SaveHelpArticleAction {
  constructor(
    @inject(TYPES.IHelpDocumentationRepository) private repo: IHelpDocumentationRepository
  ) {}

  create(data: CreateArticlePayload): Promise<HelpArticleDTO> {
    return this.repo.createArticle(data);
  }

  update(id: string, data: UpdateArticlePayload): Promise<HelpArticleDTO> {
    return this.repo.updateArticle(id, data);
  }

  delete(id: string): Promise<void> {
    return this.repo.deleteArticle(id);
  }

  updateSortBySlug(slug: string, sortOrder: number): Promise<void> {
    return this.repo.updateArticleSortBySlug(slug, sortOrder);
  }
}

@injectable()
export class GetHelpLayoutAction {
  constructor(
    @inject(TYPES.IHelpDocumentationRepository) private repo: IHelpDocumentationRepository
  ) {}

  execute(): Promise<HelpLayoutDTO> {
    return this.repo.getLayout();
  }
}

@injectable()
export class SaveHelpLayoutAction {
  constructor(
    @inject(TYPES.IHelpDocumentationRepository) private repo: IHelpDocumentationRepository
  ) {}

  execute(data: SaveHelpLayoutPayload): Promise<HelpLayoutDTO> {
    return this.repo.saveLayout(data);
  }
}
